/**
 * Whether a schedule will actually fire, as one answer.
 *
 * `isActive` and `isScheduleEnabled` are independent, and the poller requires
 * BOTH. Read as two separate flags, a deactivated schedule with the enable flag
 * set looks live in every listing while never firing, and nothing says so.
 *
 * The two stay separately editable; this is only how the pair is read. They mean
 * different things: pausing is the everyday act of stopping a schedule for a
 * while, deactivating retires it along with whatever it points at.
 */
export type ScheduleState = 'LIVE' | 'PAUSED' | 'INACTIVE';

export const scheduleStateOf = (
  schedule: { isActive?: boolean | null; isScheduleEnabled?: boolean },
): ScheduleState => {
  // Inactive wins over the enable flag: a retired schedule is not paused, and
  // calling it paused implies resuming is a toggle away when it is not.
  //
  // Only an explicit `false` counts — rows written before the field existed have
  // no value and were running, so treating a missing one as inactive would
  // retire every one of them on sight.
  if (schedule.isActive === false) return 'INACTIVE';
  return schedule.isScheduleEnabled ? 'LIVE' : 'PAUSED';
};

export const SCHEDULE_STATE_PRESENTATION: Record<
  ScheduleState, { label: string; severity: 'success' | 'warning' | 'secondary'; hint: string }
> = {
  LIVE: { label: 'Live', severity: 'success', hint: 'Active and enabled — the poller will fire it.' },
  PAUSED: { label: 'Paused', severity: 'warning', hint: 'Enabled flag is off — turn it back on to resume.' },
  INACTIVE: { label: 'Inactive', severity: 'secondary', hint: 'Deactivated — it will never fire, whatever the enable flag says.' },
};
